import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Formik, Form, Field, FieldArray } from 'formik';
import { env } from '@/config/env';

const BASE = env.API_BASE_URL + '/api/admin';

// const BASE = 'https://saracen-pickem-backend-dev.azurewebsites.net/api/admin'

async function getJson<T>(path: string, params?: Record<string, any>): Promise<T> {
  const qs = params
    ? '?' +
      new URLSearchParams(
        Object.entries(params)
          .filter(([_, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)])
      ).toString()
    : '';

  const res = await fetch(`${BASE}${path}${qs}`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

async function postJson<T>(path: string, body: any): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  const data = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })()
    : null;

  if (!res.ok) {
    const msg =
      typeof data === 'string'
        ? data
        : data && typeof data === 'object' && 'error' in (data as any)
          ? `${(data as any).error}${(data as any).message ? `: ${(data as any).message}` : ''}`
          : `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }

  return (data ?? null) as T;
}

type MarketItem = {
  id: number;
  eventId: number;
  name?: string;
  type?: string;
  subtype?: string;
  period?: string;
  displayOrder?: number;
};

type SelectionItem = {
  id: number;
  marketId: number;
  name?: string;
  displayOrder?: number;
  oddsDecimal?: number;
};

type EventItem = {
  id: number;
  name?: string;
  sport?: string;
  state?: string;
  eventTime?: number;
};

type Snapshot = {
  event: EventItem;
  markets: MarketItem[];
  selectionsByMarket: Record<string, SelectionItem[]>;
};

type QuestionType = 'team' | 'player' | 'prop' | 'tiebreaker';

type WizardValues = {
  tdc: {
    // game can be built from multiple events
    eventIds: number[];
  };
  league: { leagueId: string };
  game: { name: string; startsAt: number; sport?: string };
  questions: Array<{
    // questions must be disambiguated by event
    eventId: number;
    marketId: number;
    enabled: boolean;
    displayOrder: number;
    questionText: string;
    questionType: QuestionType;
    options: Array<{
      selectionId: number;
      enabled: boolean;
      displayOrder: number;
      optionText: string;
      oddsDecimal?: number;
    }>;
  }>;
};

const EMPTY_VALUES: WizardValues = {
  tdc: { eventIds: [] },
  league: { leagueId: '715f2632-9e64-41bb-82fc-18db16d0555a' },
  game: { name: '', startsAt: Date.now(), sport: '' },
  questions: [],
};

function classNames(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(' ');
}

const inputClass =
  'w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-600';

/**
 * Stable key for a question/market across multiple events
 */
function qKey(eventId: number, marketId: number) {
  return `${eventId}:${marketId}`;
}

function buildQuestionsFromSnapshots(snaps: Snapshot[]) {
  const out: WizardValues['questions'] = [];

  for (const snap of snaps) {
    const markets = (snap.markets ?? [])
      .slice()
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    for (const m of markets) {
      const selections = snap.selectionsByMarket[String(m.id)] ?? [];
      if (!selections.length) continue;

      const options = selections
        .slice()
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .map(s => ({
          selectionId: s.id,
          enabled: false, // default OFF
          displayOrder: s.displayOrder ?? 0,
          optionText: s.name ?? '',
          oddsDecimal: s.oddsDecimal,
        }));

      out.push({
        eventId: snap.event.id,
        marketId: m.id,
        enabled: false, // default OFF
        displayOrder: m.displayOrder ?? 0,
        questionText: m.name ?? '',
        questionType: 'team' as const,
        options,
      });
    }
  }

  // Sort: group by event then by display order
  out.sort((a, b) => {
    if (a.eventId !== b.eventId) return a.eventId - b.eventId;
    return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
  });

  return out;
}

/**
 * Best-effort merge so adding/removing events doesn’t nuke edits.
 * - matches questions by (eventId, marketId)
 * - matches options by selectionId
 */
function mergeQuestions(
  existing: WizardValues['questions'],
  generated: WizardValues['questions']
): WizardValues['questions'] {
  const existingByKey = new Map<string, WizardValues['questions'][number]>();
  for (const q of existing) existingByKey.set(qKey(q.eventId, q.marketId), q);

  return generated.map(gq => {
    const eq = existingByKey.get(qKey(gq.eventId, gq.marketId));
    if (!eq) return gq;

    const existingOptionsById = new Map<number, (typeof eq.options)[number]>();
    for (const o of eq.options) existingOptionsById.set(o.selectionId, o);

    return {
      ...gq,
      // preserve user edits
      enabled: eq.enabled,
      displayOrder: eq.displayOrder,
      questionText: eq.questionText,
      questionType: eq.questionType,
      options: gq.options.map(go => {
        const eo = existingOptionsById.get(go.selectionId);
        if (!eo) return go;
        return {
          ...go,
          enabled: eo.enabled,
          displayOrder: eo.displayOrder,
          optionText: eo.optionText,
        };
      }),
    };
  });
}

function buildValuesFromSnapshots(snaps: Snapshot[], prev?: WizardValues): WizardValues {
  const eventIds = snaps.map(s => s.event.id);

  // Game metadata: use earliest start time as default
  const times = snaps.map(s => s.event.eventTime).filter((t): t is number => typeof t === 'number');
  const startsAt = times.length ? Math.min(...times) : Date.now();

  const firstNamed = snaps.find(s => (s.event.name ?? '').trim().length > 0)?.event.name;
  const gameName =
    prev?.game?.name && prev.game.name.trim().length > 0
      ? prev.game.name
      : firstNamed
        ? `${firstNamed} (multi-event)`
        : `Multi-event game (${eventIds.length} events)`;

  const sportGuess =
    prev?.game?.sport && prev.game.sport.trim().length > 0
      ? prev.game.sport
      : snaps.find(s => s.event.sport)?.event.sport;

  const generatedQuestions = buildQuestionsFromSnapshots(snaps);
  const mergedQuestions = prev
    ? mergeQuestions(prev.questions, generatedQuestions)
    : generatedQuestions;

  return {
    tdc: { eventIds },
    league: { leagueId: prev?.league?.leagueId ?? EMPTY_VALUES.league.leagueId },
    game: {
      name: gameName,
      startsAt: prev?.game?.startsAt ?? startsAt,
      sport: sportGuess ?? '',
    },
    questions: mergedQuestions,
  };
}

function parseEventIdsFromSearch(v: unknown): number[] {
  if (typeof v !== 'string') return [];
  return Array.from(
    new Set(
      v
        .split(',')
        .map(x => Number(x.trim()))
        .filter(n => Number.isFinite(n) && n > 0)
    )
  );
}

export const Route = createFileRoute('/dashboard/feeds/$eventId/build')({
  component: FeedGameBuilderPage,
  // (optional) validateSearch / loader / etc
});

function FeedGameBuilderPage() {
  const navigate = Route.useNavigate();
  const { eventId: eventIdParam } = Route.useParams();

  // ✅ read qid AND eventIds from URL
  const search = Route.useSearch() as Partial<{ qid: number; eventIds: string }>;
  const selectedIdxFromUrl = typeof search.qid === 'number' ? search.qid : 0;

  const seedEventId = Number(eventIdParam);
  const eventIdsFromUrl = parseEventIdsFromSearch(search.eventIds);

  // ✅ Selected events (seeded by URL eventIds if present, else by route param)
  const [selectedEventIds, setSelectedEventIds] = React.useState<number[]>(() => {
    if (eventIdsFromUrl.length > 0) return eventIdsFromUrl;
    return Number.isFinite(seedEventId) && seedEventId > 0 ? [seedEventId] : [];
  });

  // ✅ Keep selectedEventIds in sync when URL changes (e.g. picker navigates with eventIds)
  React.useEffect(() => {
    const next =
      eventIdsFromUrl.length > 0
        ? eventIdsFromUrl
        : Number.isFinite(seedEventId) && seedEventId > 0
          ? [seedEventId]
          : [];

    setSelectedEventIds(prev => {
      const a = prev
        .slice()
        .sort((x, y) => x - y)
        .join(',');
      const b = next
        .slice()
        .sort((x, y) => x - y)
        .join(',');
      return a === b ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedEventId, search.eventIds]);

  // Add-by-ID input (fastest rescue)
  const [eventIdInput, setEventIdInput] = React.useState<string>('');

  function setSelectedQuestionIndex(qid: number) {
    navigate({
      search: (prev: any) => ({ ...prev, qid }),
      replace: true,
    });
  }

  // Fetch snapshots for all selected events
  const snapshotsQ = useQuery({
    queryKey: [
      'feeds',
      'snapshots',
      selectedEventIds
        .slice()
        .sort((a, b) => a - b)
        .join(','),
    ],
    queryFn: async () => {
      const ids = Array.from(new Set(selectedEventIds)).filter(n => Number.isFinite(n) && n > 0);
      if (ids.length === 0) return [] as Snapshot[];
      const snaps = await Promise.all(ids.map(id => getJson<Snapshot>(`/events/${id}/snapshot`)));
      return snaps;
    },
    enabled: selectedEventIds.length > 0,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="p-4 space-y-4 max-w-6xl">
      <div className="flex items-center gap-3">
        <button
          className="px-3 py-1.5 rounded border border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
          onClick={() => navigate({ to: '/dashboard/feeds' })}
          type="button"
        >
          ← Back
        </button>
        <div>
          <h2 className="text-lg font-semibold text-slate-100">Build Game</h2>
          <div className="text-sm text-slate-500">
            This game can include markets from multiple TDC events.
          </div>
        </div>
      </div>

      {/* Event selection bar */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-lg shadow-black/20">
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-3 flex-wrap">
            <label className="grid gap-1">
              <span className="text-sm text-slate-400">Add event by ID</span>
              <input
                value={eventIdInput}
                onChange={e => setEventIdInput(e.target.value)}
                placeholder="e.g. 12345"
                className={inputClass}
                inputMode="numeric"
              />
            </label>

            <button
              type="button"
              className="px-3 py-2 rounded border border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800"
              onClick={() => {
                const n = Number(eventIdInput.trim());
                if (!Number.isFinite(n) || n <= 0) return;
                setSelectedEventIds(prev => (prev.includes(n) ? prev : [...prev, n]));
                setEventIdInput('');
              }}
            >
              Add event
            </button>

            <button
              type="button"
              className="px-3 py-2 rounded border border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800"
              onClick={() => {
                // quick reset to only seed event
                if (Number.isFinite(seedEventId) && seedEventId > 0)
                  setSelectedEventIds([seedEventId]);
              }}
            >
              Reset to seed
            </button>
          </div>

          <div>
            <div className="text-sm text-slate-400">Selected events</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedEventIds.length === 0 ? (
                <div className="text-slate-500 text-sm">None selected.</div>
              ) : (
                selectedEventIds.map(id => (
                  <div
                    key={id}
                    className="flex items-center gap-2 rounded-full border border-slate-700 bg-slate-950 px-3 py-1.5"
                  >
                    <span className="text-sm text-slate-200">#{id}</span>
                    <button
                      type="button"
                      className="text-slate-400 hover:text-slate-200"
                      onClick={() => setSelectedEventIds(prev => prev.filter(x => x !== id))}
                      aria-label={`Remove event ${id}`}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Tip: this is “add by ID” to get you unblocked. You can swap it for an event search UI
              later.
            </div>
          </div>
        </div>
      </div>

      {snapshotsQ.isLoading && <div className="text-slate-400">Loading snapshots…</div>}
      {snapshotsQ.isError && (
        <div className="text-red-400">Error: {(snapshotsQ.error as Error).message}</div>
      )}

      {snapshotsQ.data && snapshotsQ.data.length > 0 && (
        <Formik
          initialValues={buildValuesFromSnapshots(snapshotsQ.data)}
          enableReinitialize={false} // IMPORTANT: we merge manually so we don't nuke edits
          onSubmit={async (values, helpers) => {
            try {
              const leagueId = String((values as any).league?.leagueId ?? '').trim();
              const eventIds = Array.from(new Set(values.tdc.eventIds)).filter(
                n => Number.isFinite(n) && n > 0
              );

              if (!leagueId) throw new Error('Missing leagueId');
              if (eventIds.length === 0) throw new Error('Select at least 1 event');

              const marketSelections = values.questions
                .filter(q => q.enabled)
                .map(q => {
                  const selectionIds = q.options.filter(o => o.enabled).map(o => o.selectionId);
                  return {
                    eventId: q.eventId, // NEW
                    marketId: q.marketId,
                    selectionIds: selectionIds.length > 0 ? selectionIds : undefined,
                    questionType: q.questionType,
                  };
                });

              if (marketSelections.length === 0) {
                throw new Error('Select at least 1 market (question).');
              }

              const body = {
                leagueId,
                eventIds, // NEW
                marketSelections, // NEW: includes eventId
                titleEn: values.game.name || undefined,
                startUtc: new Date(values.game.startsAt).toISOString(),
                openPickemUtc: new Date(values.game.startsAt).toISOString(),
              };

              type GenerateResp = {
                pickemId: string;
                eventIds?: number[];
                marketCount: number;
                selectionCount: number;
              };

              const resp = await postJson<GenerateResp>('/feeds/pickems/generate', body);

              alert(
                `Created pickem ${resp.pickemId}\nMarkets: ${resp.marketCount}\nSelections: ${resp.selectionCount}`
              );
            } catch (err) {
              alert(`Create failed: ${(err as Error).message}`);
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {({ values, setValues, setFieldValue, isSubmitting }) => {
            // Merge in snapshot changes whenever selected events change / snapshots refetch
            React.useEffect(() => {
              if (!snapshotsQ.data) return;
              setValues((prev: any) => buildValuesFromSnapshots(snapshotsQ.data!, prev), false);
              setSelectedQuestionIndex(0);
              // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [snapshotsQ.data]);

            const totalQuestions = values.questions.length;
            const selectedIdx = Math.min(
              Math.max(0, selectedIdxFromUrl),
              Math.max(0, totalQuestions - 1)
            );
            const selected = values.questions[selectedIdx];

            const enabledQuestions = values.questions.filter(q => q.enabled);
            const enabledOptionsCount = enabledQuestions.reduce(
              (sum, q) => sum + q.options.filter(o => o.enabled).length,
              0
            );

            const toggleAll = (on: boolean) => {
              values.questions.forEach((q, idx) => {
                setFieldValue(`questions.${idx}.enabled`, on, false);
                q.options.forEach((_, oi) => {
                  setFieldValue(`questions.${idx}.options.${oi}.enabled`, on, false);
                });
              });
            };

            // Map event metadata for display
            const eventMetaById = new Map<number, EventItem>();
            for (const s of snapshotsQ.data ?? []) eventMetaById.set(s.event.id, s.event);

            // Map market meta by (eventId, marketId)
            const marketMetaByKey = new Map<string, MarketItem>();
            for (const s of snapshotsQ.data ?? []) {
              for (const m of s.markets ?? []) {
                marketMetaByKey.set(qKey(s.event.id, m.id), { ...m, eventId: s.event.id });
              }
            }

            return (
              <Form className="space-y-4">
                {/* Game header */}
                <div className="rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-lg shadow-black/20">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="mt-1 font-semibold text-slate-100">
                        Game built from {values.tdc.eventIds.length} event(s)
                      </div>
                      <div className="text-sm text-slate-400 mt-1">
                        {values.tdc.eventIds
                          .map(id => {
                            const e = eventMetaById.get(id);
                            const label = e?.name ? `${e.name}` : `Event #${id}`;
                            return label;
                          })
                          .join(' • ')}
                      </div>
                    </div>

                    <div className="text-xs text-slate-400">
                      Event IDs:{' '}
                      <span className="text-slate-300">{values.tdc.eventIds.join(', ')}</span>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4">
                    <label className="grid gap-1">
                      <span className="text-sm text-slate-400">Game name</span>
                      <Field name="game.name" className={inputClass} />
                    </label>

                    <div className="grid gap-4 md:grid-cols-3">
                      <label className="grid gap-1">
                        <span className="text-sm text-slate-400">Sport (optional)</span>
                        <Field name="game.sport" className={inputClass} />
                      </label>

                      <label className="grid gap-1 md:col-span-2">
                        <span className="text-sm text-slate-400">Starts at</span>
                        <input
                          type="datetime-local"
                          value={
                            values.game.startsAt
                              ? new Date(values.game.startsAt).toISOString().slice(0, 16)
                              : ''
                          }
                          onChange={e =>
                            setFieldValue(
                              'game.startsAt',
                              new Date(e.target.value).getTime(),
                              false
                            )
                          }
                          className={inputClass}
                        />
                      </label>
                    </div>

                    <div className="text-xs text-slate-500">
                      {values.game.startsAt ? new Date(values.game.startsAt).toLocaleString() : '—'}
                    </div>
                  </div>
                </div>

                {/* Main builder */}
                <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
                  {/* Questions list */}
                  <div className="rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-lg shadow-black/20">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-slate-100">Questions</div>
                        <div className="text-sm text-slate-500">
                          {enabledQuestions.length} enabled • {enabledOptionsCount} options enabled
                        </div>

                        <div className="mt-3 flex gap-2">
                          <button
                            type="button"
                            className="px-2.5 py-1.5 rounded border border-slate-700 bg-slate-950 text-slate-200 text-sm hover:bg-slate-800"
                            onClick={() => toggleAll(true)}
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            className="px-2.5 py-1.5 rounded border border-slate-700 bg-slate-950 text-slate-200 text-sm hover:bg-slate-800"
                            onClick={() => toggleAll(false)}
                          >
                            Select none
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2 max-h-[520px] overflow-auto pr-1">
                      {values.questions.map((q, idx) => {
                        const isSelected = idx === selectedIdx;
                        const eventMeta = eventMetaById.get(q.eventId);
                        const marketMeta = marketMetaByKey.get(qKey(q.eventId, q.marketId));

                        return (
                          <button
                            key={qKey(q.eventId, q.marketId)}
                            type="button"
                            onClick={() => setSelectedQuestionIndex(idx)}
                            className={classNames(
                              'w-full text-left rounded-xl border px-4 py-3 transition',
                              isSelected
                                ? 'border-slate-500 bg-slate-800'
                                : 'border-slate-700 bg-slate-950 hover:bg-slate-800'
                            )}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={q.enabled}
                                onChange={e => {
                                  const on = e.target.checked;
                                  setFieldValue(`questions.${idx}.enabled`, on, false);
                                  q.options.forEach((_, oi) => {
                                    setFieldValue(
                                      `questions.${idx}.options.${oi}.enabled`,
                                      on,
                                      false
                                    );
                                  });
                                }}
                                className="mt-1"
                              />

                              <div className="min-w-0 flex-1">
                                <div className="text-xs text-slate-400 truncate">
                                  {eventMeta?.name ?? `Event #${q.eventId}`}
                                </div>

                                <div className="font-medium truncate text-slate-200 mt-1">
                                  {marketMeta?.name ?? q.questionText ?? '(Unnamed market)'}
                                </div>

                                <div className="text-xs text-slate-500 mt-1 truncate">
                                  {marketMeta?.type ?? '—'}
                                  {marketMeta?.subtype ? ` • ${marketMeta.subtype}` : ''}
                                  {marketMeta?.period ? ` • period: ${marketMeta.period}` : ''}
                                </div>

                                <div className="text-xs text-slate-500 mt-1">
                                  {q.options.filter(o => o.enabled).length}/{q.options.length}{' '}
                                  options enabled • Market {q.marketId}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Selected question editor */}
                  <div className="rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-lg shadow-black/20">
                    {!selected ? (
                      <div className="text-slate-400">No questions available for these events.</div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-semibold text-slate-100">Edit question</div>
                            <div className="text-sm text-slate-500">
                              Event #{selected.eventId} • Market #{selected.marketId} •{' '}
                              {selected.options.length} options
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 grid gap-3">
                          <label className="grid gap-1">
                            <span className="text-sm text-slate-400">Question text</span>
                            <Field
                              name={`questions.${selectedIdx}.questionText`}
                              className={inputClass}
                            />
                          </label>

                          <label className="grid gap-1">
                            <span className="text-sm text-slate-400">Question type</span>
                            <Field
                              as="select"
                              name={`questions.${selectedIdx}.questionType`}
                              className={inputClass}
                            >
                              <option value="team">Team Selection</option>
                              <option value="player">Player Selection</option>
                              <option value="prop">Yes/No Proposition</option>
                              <option value="tiebreaker">Tiebreaker</option>
                            </Field>
                          </label>

                          <div className="grid gap-3 sm:grid-cols-3 items-end">
                            <label className="grid gap-1 sm:col-span-1">
                              <span className="text-sm text-slate-400">Display order</span>
                              <Field
                                name={`questions.${selectedIdx}.displayOrder`}
                                type="number"
                                className={inputClass}
                              />
                            </label>

                            <label className="flex items-center gap-2 sm:col-span-2 text-slate-300">
                              <Field name={`questions.${selectedIdx}.enabled`} type="checkbox" />
                              <span className="text-sm">Enabled</span>
                            </label>
                          </div>
                        </div>

                        <div className="mt-6">
                          <div className="font-semibold text-slate-100">Options</div>
                          <div className="text-sm text-slate-500">
                            Edit names, disable anything you don’t want.
                          </div>

                          <FieldArray name={`questions.${selectedIdx}.options`}>
                            {() => (
                              <div className="mt-3 space-y-2">
                                {selected.options.map((o, oi) => (
                                  <div
                                    key={o.selectionId}
                                    className="rounded-xl border border-slate-700 bg-slate-950 p-4"
                                  >
                                    <div className="flex items-center justify-between gap-3">
                                      <label className="flex items-center gap-2 text-slate-200">
                                        <Field
                                          name={`questions.${selectedIdx}.options.${oi}.enabled`}
                                          type="checkbox"
                                        />
                                        <span className="text-sm font-medium">
                                          Selection #{o.selectionId}
                                        </span>
                                      </label>
                                      {typeof o.oddsDecimal === 'number' ? (
                                        <span className="text-xs text-slate-500">
                                          odds: {o.oddsDecimal}
                                        </span>
                                      ) : null}
                                    </div>

                                    <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_140px]">
                                      <label className="grid gap-1">
                                        <span className="text-xs text-slate-500">Option text</span>
                                        <Field
                                          name={`questions.${selectedIdx}.options.${oi}.optionText`}
                                          className={inputClass}
                                        />
                                      </label>

                                      <label className="grid gap-1">
                                        <span className="text-xs text-slate-500">
                                          Display order
                                        </span>
                                        <Field
                                          name={`questions.${selectedIdx}.options.${oi}.displayOrder`}
                                          type="number"
                                          className={inputClass}
                                        />
                                      </label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer review */}
                <div className="rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-lg shadow-black/20 flex items-center justify-between">
                  <div className="text-sm text-slate-200">
                    <span className="font-medium">Ready?</span> {enabledQuestions.length} questions
                    enabled, {enabledOptionsCount} options enabled.
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={classNames(
                      'px-4 py-2 rounded font-medium',
                      isSubmitting
                        ? 'bg-slate-800 text-slate-400'
                        : 'bg-slate-950 text-white hover:bg-slate-800'
                    )}
                  >
                    {isSubmitting ? 'Creating…' : 'Create game'}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
    </div>
  );
}

export default FeedGameBuilderPage;
